import { type Dispatch, type SetStateAction, useState } from "react";

import {
  type WorkflowStepInput,
  type WorkflowTemplate,
  WorkflowStepStatuses,
} from "~/api/types";
import { useWorkflowUsers } from "~/hooks/useWorkflowUsers";

const DEFAULT_STEP: WorkflowStepInput = {
  _key: "default-0",
  name: "Step 1",
  description: "",
  status: WorkflowStepStatuses.upcoming,
  type: "review",
  order: 0,
  dueAt: null,
  users: [],
};

interface TemplateEditorState {
  editedSteps: WorkflowStepInput[];
  setEditedSteps: Dispatch<SetStateAction<WorkflowStepInput[]>>;
  editedName: string;
  setEditedName: Dispatch<SetStateAction<string>>;
  editedDescription: string | null;
  setEditedDescription: Dispatch<SetStateAction<string | null>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  isSaving: boolean;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  lastLoadedTemplateId: number | null;
  setLastLoadedTemplateId: Dispatch<SetStateAction<number | null>>;
  /** Load state from a fetched template detail */
  loadFromTemplate: (template: WorkflowTemplate) => void;
  /** Reset state for a brand-new template */
  resetForNewTemplate: () => void;
}

export function useTemplateEditor(): TemplateEditorState {
  const { hydrateSteps } = useWorkflowUsers();

  const [editedSteps, setEditedSteps] = useState<WorkflowStepInput[]>([]);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState<string | null>(
    null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastLoadedTemplateId, setLastLoadedTemplateId] = useState<
    number | null
  >(null);

  function loadFromTemplate(template: WorkflowTemplate): void {
    setEditedSteps(hydrateSteps(template));
    setEditedName(template.name ?? "");
    setEditedDescription(template.description);
    setHasUnsavedChanges(false);
    setLastLoadedTemplateId(template.id);
  }

  function resetForNewTemplate(): void {
    setEditedSteps([{ ...DEFAULT_STEP }]);
    setEditedName("");
    setEditedDescription(null);
    setHasUnsavedChanges(false);
    setLastLoadedTemplateId(null);
  }

  return {
    editedSteps,
    setEditedSteps,
    editedName,
    setEditedName,
    editedDescription,
    setEditedDescription,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isSaving,
    setIsSaving,
    lastLoadedTemplateId,
    setLastLoadedTemplateId,
    loadFromTemplate,
    resetForNewTemplate,
  };
}
